<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { externalCredentialsAPI } from 'dashboard/api/externalCredentials';

import Button from 'dashboard/components-next/button/Button.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import {
  BaseTable,
  BaseTableCell,
  BaseTableRow,
} from 'dashboard/components-next/table';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import ExternalCredentialDialog from './ExternalCredentialDialog.vue';

defineOptions({ name: 'ExternalCredentialsSettings' });

const { t } = useI18n();
const credentials = ref([]);
const templates = ref([]);
const page = ref(1);
const perPage = ref(25);
const total = ref(0);
const isLoading = ref(false);
const isSaving = ref(false);
const testingId = ref(null);
const deletingId = ref(null);
const editingCredential = ref(null);
const showDialog = ref(false);
const showDeleteConfirmation = ref(false);
const credentialToDelete = ref(null);

const tableHeaders = computed(() => [
  t('EXTERNAL_CREDENTIALS.TABLE.NAME'),
  t('EXTERNAL_CREDENTIALS.TABLE.TYPE'),
  t('EXTERNAL_CREDENTIALS.TABLE.STATUS'),
  t('EXTERNAL_CREDENTIALS.TABLE.CONFIGURATION'),
  t('EXTERNAL_CREDENTIALS.TABLE.ACTIONS'),
]);

const typeLabel = credential =>
  t(
    `EXTERNAL_CREDENTIALS.PROVIDERS.${credential.provider}`,
    credential.provider
  );

const loadCredentials = async () => {
  isLoading.value = true;
  try {
    const { data } = await externalCredentialsAPI.list({
      page: page.value,
      per_page: perPage.value,
    });
    credentials.value = data.payload;
    total.value = data.meta.total;
  } catch {
    useAlert(t('EXTERNAL_CREDENTIALS.API.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const loadTemplates = async () => {
  try {
    const { data } = await externalCredentialsAPI.templates();
    templates.value = data;
  } catch {
    useAlert(t('EXTERNAL_CREDENTIALS.API.TEMPLATES_ERROR'));
  }
};

const openCreate = () => {
  editingCredential.value = null;
  showDialog.value = true;
};

const openEdit = credential => {
  editingCredential.value = credential;
  showDialog.value = true;
};

const closeDialog = () => {
  showDialog.value = false;
  editingCredential.value = null;
};

const saveCredential = async payload => {
  isSaving.value = true;
  try {
    if (editingCredential.value) {
      await externalCredentialsAPI.update(editingCredential.value.id, payload);
    } else {
      await externalCredentialsAPI.create(payload);
    }
    useAlert(t('EXTERNAL_CREDENTIALS.API.SAVED'));
    closeDialog();
    await loadCredentials();
  } catch (error) {
    const message = error?.response?.data?.errors?.[0];
    useAlert(message || t('EXTERNAL_CREDENTIALS.API.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const testCredential = async credential => {
  testingId.value = credential.id;
  try {
    const { data } = await externalCredentialsAPI.test(credential.id);
    useAlert(t(`EXTERNAL_CREDENTIALS.TEST_RESULTS.${data.code}`, data.code));
  } catch (error) {
    const code = error?.response?.data?.code || 'connection_failed';
    useAlert(t(`EXTERNAL_CREDENTIALS.TEST_RESULTS.${code}`, code));
  } finally {
    testingId.value = null;
  }
};

const requestDelete = credential => {
  credentialToDelete.value = credential;
  showDeleteConfirmation.value = true;
};

const deleteCredential = async () => {
  const credential = credentialToDelete.value;
  showDeleteConfirmation.value = false;
  deletingId.value = credential.id;
  try {
    await externalCredentialsAPI.delete(credential.id);
    useAlert(t('EXTERNAL_CREDENTIALS.API.DELETED'));
    if (credentials.value.length === 1 && page.value > 1) page.value -= 1;
    await loadCredentials();
  } catch (error) {
    const inUse = error?.response?.data?.error === 'credential_in_use';
    useAlert(
      t(
        inUse
          ? 'EXTERNAL_CREDENTIALS.API.IN_USE'
          : 'EXTERNAL_CREDENTIALS.API.DELETE_ERROR'
      )
    );
  } finally {
    deletingId.value = null;
    credentialToDelete.value = null;
  }
};

const changePage = () => loadCredentials();

onMounted(() => Promise.all([loadCredentials(), loadTemplates()]));
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="t('EXTERNAL_CREDENTIALS.LOADING')"
    :no-records-found="!credentials.length"
    :no-records-message="t('EXTERNAL_CREDENTIALS.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('EXTERNAL_CREDENTIALS.TITLE')"
        :description="t('EXTERNAL_CREDENTIALS.DESCRIPTION')"
      >
        <template #count>
          <span class="text-body-main text-n-slate-11">
            {{ t('EXTERNAL_CREDENTIALS.COUNT', { count: total }) }}
          </span>
        </template>
        <template #actions>
          <Button
            size="sm"
            icon="i-lucide-plus"
            :label="t('EXTERNAL_CREDENTIALS.CREATE')"
            @click="openCreate"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <section class="overflow-hidden rounded-lg border border-n-weak">
        <BaseTable :headers="tableHeaders" :items="credentials">
          <template #row="{ items }">
            <BaseTableRow
              v-for="credential in items"
              :key="credential.id"
              :item="credential"
            >
              <BaseTableCell class="max-w-0">
                <div class="min-w-0">
                  <p class="truncate text-sm font-medium text-n-slate-12">
                    {{ credential.name }}
                  </p>
                  <p class="truncate text-sm text-n-slate-11">
                    {{ credential.base_url || typeLabel(credential) }}
                  </p>
                </div>
              </BaseTableCell>
              <BaseTableCell>{{ typeLabel(credential) }}</BaseTableCell>
              <BaseTableCell>
                {{ t(`EXTERNAL_CREDENTIALS.STATUSES.${credential.status}`) }}
              </BaseTableCell>
              <BaseTableCell>
                {{
                  t(
                    credential.configured
                      ? 'EXTERNAL_CREDENTIALS.CONFIGURED'
                      : 'EXTERNAL_CREDENTIALS.NOT_CONFIGURED'
                  )
                }}
              </BaseTableCell>
              <BaseTableCell align="end">
                <div class="flex justify-end gap-1">
                  <Button
                    v-if="credential.testable"
                    size="sm"
                    color="slate"
                    variant="ghost"
                    icon="i-lucide-plug-zap"
                    :is-loading="testingId === credential.id"
                    :aria-label="t('EXTERNAL_CREDENTIALS.TEST')"
                    @click="testCredential(credential)"
                  />
                  <Button
                    size="sm"
                    color="slate"
                    variant="ghost"
                    icon="i-lucide-pencil"
                    :aria-label="t('EXTERNAL_CREDENTIALS.EDIT')"
                    @click="openEdit(credential)"
                  />
                  <Button
                    size="sm"
                    color="ruby"
                    variant="ghost"
                    icon="i-lucide-trash-2"
                    :is-loading="deletingId === credential.id"
                    :aria-label="t('EXTERNAL_CREDENTIALS.DELETE')"
                    @click="requestDelete(credential)"
                  />
                </div>
              </BaseTableCell>
            </BaseTableRow>
          </template>
        </BaseTable>
        <PaginationFooter
          v-model:current-page="page"
          v-model:items-per-page="perPage"
          :total-items="total"
          show-page-size-selector
          @update:current-page="changePage"
          @update:items-per-page="changePage"
        />
      </section>
    </template>

    <ExternalCredentialDialog
      v-if="showDialog"
      :credential="editingCredential"
      :templates="templates"
      :is-loading="isSaving"
      @save="saveCredential"
      @close="closeDialog"
    />

    <woot-delete-modal
      v-model:show="showDeleteConfirmation"
      :on-close="() => (showDeleteConfirmation = false)"
      :on-confirm="deleteCredential"
      :title="t('EXTERNAL_CREDENTIALS.DELETE_CONFIRM.TITLE')"
      :message="t('EXTERNAL_CREDENTIALS.DELETE_CONFIRM.MESSAGE')"
      :message-value="credentialToDelete?.name || ''"
      :confirm-text="t('EXTERNAL_CREDENTIALS.DELETE_CONFIRM.CONFIRM')"
      :reject-text="t('EXTERNAL_CREDENTIALS.DELETE_CONFIRM.CANCEL')"
    />
  </SettingsLayout>
</template>
